// The reader's own 1:1 with the facilitator (D77).
//
// A second, independent thread view beside `Conversation`: same shape of screen — the
// scrollback, the composer, "Read older" — over the reader's own solo thread instead of the
// group's.
//
// Before a group thread exists, this is also where the discussing state's own asks have to
// render: pending actions are D70's plan-wide list, and `Conversation` falls back to this
// screen until the group arrives (see `PlanScreen`), so an ask raised during discussing —
// the move-to-deciding confirmation, an invitation alongside it — would otherwise have
// nowhere to appear. Filtered to this thread's own asks, using the same row and the same
// placement `Conversation` would give them once the group exists. Once the group exists,
// an ask appears in both places at once — accepted placeholder duplication, not a bug.

import type { ApiClient } from '../api/client.js';
import type { Thread } from '../api/contract.js';
import { Composer } from '../components/Composer.js';
import { ThreadRoster } from '../components/ThreadRoster.js';
import type { Internals } from '../internals.js';
import { interleave } from '../plan/inline.js';
import type { PlanState } from '../plan/usePlan.js';
import { useFollowBottom } from '../plan/useFollowBottom.js';
import { useMarkThreadRead } from '../plan/useMarkThreadRead.js';
import { usePrivateThread } from '../plan/usePrivateThread.js';

export type PrivateChatProps = {
  client: ApiClient;
  thread: Thread | null;
  state: PlanState;
  internals: Internals;
  label?: string;
  emptyText?: string;
  /** Which room this is, for `ThreadRoster`'s heading. Defaults to the reader's own 1:1. */
  roomKind?: 'private' | 'principals';
  /** See `Conversation.tsx`'s prop of the same name — the mobile decisions strip, handed
   * down so it renders directly above the composer rather than beside the conversation. */
  decisionsStrip?: JSX.Element | null;
};

export function PrivateChat({
  client,
  thread,
  state,
  internals,
  label = 'Private chat',
  emptyText = 'This is between you and Mor. Say anything.',
  roomKind = 'private',
  decisionsStrip,
}: PrivateChatProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const own = usePrivateThread(client, thread, state.snapshot);

  // Same rule as `Conversation`, and the same reason the interleaved `items` — not
  // `own.messages` alone — feed `lastKey`: a pending action arriving with no accompanying
  // message has to move it too. Computed unconditionally, ahead of the null check below,
  // because `useFollowBottom` is a hook. `resetKey` is this thread's own id; see
  // `useFollowBottom` for why.
  //
  // An ask carries a `createdAt`, so these never sit permanently at the end the way
  // `Conversation`'s can — but the count is included in `lastKey` anyway, on the same
  // reasoning, rather than leave this screen's computation looking identical while resting
  // on an assumption `Conversation` can't make.
  const asks =
    snapshot === null
      ? []
      : snapshot.pendingActions.filter((action) => action.threadId === thread?.id);
  const items = interleave(own.messages, asks);
  const lastKey = `${items.length}:${items[items.length - 1]?.key ?? ''}`;
  const { scroller, atBottom } = useFollowBottom(lastKey, thread?.id ?? '');
  useMarkThreadRead(thread?.id ?? null, own.messages, atBottom, state.markRead);

  if (snapshot === null) {
    return null;
  }

  const { MessageRow, PendingActionRow, FacilitatorState } = internals;

  return (
    <section className="conversation" aria-label={label}>
      <ThreadRoster people={snapshot.people} kind={roomKind} />
      <div className="conversation__scroller" ref={scroller}>
        {own.nextBefore === null ? null : (
          <button
            type="button"
            className="button conversation__older"
            onClick={own.loadOlder}
          >
            Read older
          </button>
        )}
        {items.length === 0 ? (
          <p className="conversation__empty">{emptyText}</p>
        ) : (
          <ol className="conversation__items">
            {items.map((item) =>
              item.kind === 'message' ? (
                <MessageRow
                  key={item.key}
                  message={item.message}
                  people={snapshot.people}
                />
              ) : (
                <PendingActionRow
                  key={item.key}
                  action={item.action}
                  people={snapshot.people}
                  resolve={state.resolve}
                />
              ),
            )}
          </ol>
        )}
      </div>

      <div className="conversation__foot">
        <FacilitatorState facilitatorBusy={snapshot.plan.facilitatorBusy} />
        {decisionsStrip ?? null}
        {thread === null ? null : (
          <Composer posting={own.posting} sendFailed={own.sendFailed} onSend={own.post} />
        )}
      </div>
    </section>
  );
}
